"use client";

import * as React from "react";
import { Bookmark, BookmarkPlus, Check, Trash2 } from "lucide-react";
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  Label,
} from "@aio/platform-ui";
import type { SavedView, UseSavedViewsResult } from "../use-saved-views";

/**
 * Props for {@link SavedViewsMenu}.
 *
 * The menu is a thin presentational layer over {@link UseSavedViewsResult}.
 * The hook owns persistence + state — this component renders the dropdown
 * surface, hosts the "Save current view as…" dialog, and dispatches to the
 * hook's `save` / `apply` / `delete` methods.
 */
export interface SavedViewsMenuProps {
  /** The result of calling {@link useSavedViews} for this table. */
  views: UseSavedViewsResult;
  /** Trigger button label override. Defaults to a localizable English string. */
  triggerLabel?: string;
  /** Heading label inside the dropdown. Defaults to "Saved views". */
  menuLabel?: string;
  /** Save-dialog title. Defaults to "Save current view". */
  saveDialogTitle?: string;
  /** Save-dialog description. Defaults to a localizable English string. */
  saveDialogDescription?: string;
  /** Save-dialog confirm-button label. Defaults to "Save view". */
  saveButtonLabel?: string;
  /** Save-dialog cancel-button label. Defaults to "Cancel". */
  cancelButtonLabel?: string;
  /** Empty-state copy when no views exist yet. */
  emptyLabel?: string;
  /** Save-as item label. Defaults to "Save current view as…". */
  saveAsLabel?: string;
  /** Optional placeholder text for the name input. */
  inputPlaceholder?: string;
  /** Extra classes for the trigger button. */
  className?: string;
}

/**
 * Toolbar dropdown listing saved table views with apply / delete actions.
 *
 * Behaviour:
 * - Trigger renders a "Saved views" button with a bookmark icon.
 * - When views exist, each is rendered as a `DropdownMenuItem` with the
 *   view name on the left and a trash button on the right. Clicking the
 *   row calls `views.apply(view.id)`. Clicking the trash button calls
 *   `views.delete(view.id)` and *does not* close the menu (so a user can
 *   prune several views without re-opening).
 * - The currently-applied view shows a check glyph next to its name.
 * - "Save current view as…" opens a small modal dialog with a name input.
 *   Submitting calls `views.save(name)` and closes the dialog.
 * - When no views exist yet, the dropdown shows a friendly empty-state
 *   line above the save action.
 *
 * Save-dialog focus management: the input is auto-focused when the dialog
 * opens, and Enter triggers save (preventing default form submission keeps
 * the dialog from refreshing the page when the menu is hosted inside a
 * form).
 */
export function SavedViewsMenu({
  views,
  triggerLabel = "Saved views",
  menuLabel = "Saved views",
  saveDialogTitle = "Save current view",
  saveDialogDescription = "Give this view a name. You'll be able to apply it later from the Saved views menu.",
  saveButtonLabel = "Save view",
  cancelButtonLabel = "Cancel",
  emptyLabel = "No saved views yet.",
  saveAsLabel = "Save current view as…",
  inputPlaceholder = "View name",
  className,
}: SavedViewsMenuProps): React.JSX.Element {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [name, setName] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  // Track the trigger element so we can restore focus after the dialog closes
  // — Radix Dialog's autoFocus return is sometimes shadowed by the dropdown
  // closing animation, and explicit restoration avoids the focus landing on
  // <body>.
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);

  // Reset the input whenever the dialog transitions to open. Closing keeps
  // the previous value visible during the close animation; that's fine.
  React.useEffect(() => {
    if (dialogOpen) {
      setName("");
      setError(null);
    }
  }, [dialogOpen]);

  const handleSave = React.useCallback(() => {
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      setError("Please enter a name for this view.");
      return;
    }
    views.save(trimmed);
    setDialogOpen(false);
  }, [name, views]);

  const onInputKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        // Don't let Enter submit any enclosing form when the dialog is
        // rendered inside a page-level form (rare but possible in the
        // platform-form integration that lands later).
        e.preventDefault();
        handleSave();
      }
    },
    [handleSave],
  );

  const currentId = views.current?.id ?? null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            ref={triggerRef}
            variant="outline"
            size="sm"
            className={cn("gap-2", className)}
            data-testid="saved-views-trigger"
          >
            <Bookmark className="size-4" aria-hidden="true" />
            <span>{triggerLabel}</span>
            {views.views.length > 0 ? (
              <span
                className="ml-1 inline-flex size-5 items-center justify-center rounded-full bg-muted text-xs font-medium text-fg-muted"
                aria-hidden="true"
              >
                {views.views.length}
              </span>
            ) : null}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-64">
          <DropdownMenuLabel>{menuLabel}</DropdownMenuLabel>
          <DropdownMenuSeparator />

          {views.views.length === 0 ? (
            <div
              className="px-2 py-3 text-xs text-fg-muted"
              data-testid="saved-views-empty"
            >
              {emptyLabel}
            </div>
          ) : (
            views.views.map((view) => (
              <SavedViewRow
                key={view.id}
                view={view}
                isCurrent={view.id === currentId}
                onApply={() => views.apply(view.id)}
                onDelete={() => views.delete(view.id)}
              />
            ))
          )}

          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={(event) => {
              // Prevent Radix from immediately closing — we open the dialog
              // first, then close the menu in the next tick. Without this,
              // the autoFocus on the dialog input fights with the menu's
              // closing focus restoration and the input never receives focus.
              event.preventDefault();
              setDialogOpen(true);
            }}
            data-testid="saved-views-save-as"
          >
            <BookmarkPlus className="size-4" aria-hidden="true" />
            <span>{saveAsLabel}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          // Restore focus to the trigger when the dialog closes so keyboard
          // users land back where they started instead of on <body>.
          if (!open) {
            // Defer to next frame so Radix's own focus restoration runs first
            // (otherwise we'd race it).
            queueMicrotask(() => triggerRef.current?.focus());
          }
        }}
      >
        <DialogContent
          data-testid="saved-views-save-dialog"
          // Trap Tab inside the dialog (Radix default) and let Esc close it.
          onOpenAutoFocus={(e) => {
            // Default Radix behaviour is to focus the first focusable element
            // — which is the close button. We want the name input instead.
            e.preventDefault();
            // Using a short delay so the input is mounted before we reach for
            // it. queueMicrotask isn't enough on Radix portals.
            requestAnimationFrame(() => {
              const input = document.querySelector<HTMLInputElement>(
                '[data-testid="saved-views-name-input"]',
              );
              input?.focus();
              input?.select();
            });
          }}
        >
          <DialogHeader>
            <DialogTitle>{saveDialogTitle}</DialogTitle>
            <DialogDescription>{saveDialogDescription}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            <Label htmlFor="saved-views-name-input">Name</Label>
            <Input
              id="saved-views-name-input"
              data-testid="saved-views-name-input"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (error) setError(null);
              }}
              onKeyDown={onInputKeyDown}
              placeholder={inputPlaceholder}
              aria-invalid={error ? true : undefined}
              aria-describedby={
                error ? "saved-views-name-error" : undefined
              }
            />
            {error ? (
              <p
                id="saved-views-name-error"
                role="alert"
                className="text-xs text-danger"
                data-testid="saved-views-name-error"
              >
                {error}
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              data-testid="saved-views-cancel"
            >
              {cancelButtonLabel}
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              data-testid="saved-views-confirm"
            >
              {saveButtonLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * A single saved-view row inside the dropdown.
 *
 * Encapsulated in its own component so the trash button can stop event
 * propagation cleanly without growing the parent JSX into another nested
 * function. The whole row is the "apply" affordance; the trash icon at the
 * right does NOT bubble back to the row's onSelect.
 */
function SavedViewRow({
  view,
  isCurrent,
  onApply,
  onDelete,
}: {
  view: SavedView;
  isCurrent: boolean;
  onApply: () => void;
  onDelete: () => void;
}): React.JSX.Element {
  return (
    <DropdownMenuItem
      onSelect={onApply}
      data-testid={`saved-views-item-${view.id}`}
      className="flex items-center justify-between gap-2 pr-1"
    >
      <span className="flex items-center gap-2 truncate">
        {isCurrent ? (
          <Check
            className="size-4 text-accent"
            aria-label="Currently applied"
            data-testid={`saved-views-current-${view.id}`}
          />
        ) : (
          // Spacer so view names line up regardless of whether the check
          // glyph is rendered. Using an aria-hidden div instead of CSS
          // padding keeps the icon and text visually centred.
          <span className="inline-block size-4" aria-hidden="true" />
        )}
        <span className="truncate text-sm">{view.name}</span>
      </span>
      <Button
        variant="ghost"
        size="icon"
        className="size-7 shrink-0"
        aria-label={`Delete view ${view.name}`}
        data-testid={`saved-views-delete-${view.id}`}
        onClick={(e) => {
          // Stop the click bubbling to the DropdownMenuItem (which would
          // call onApply) and prevent Radix from closing the dropdown so
          // the user can prune multiple views in one session.
          e.preventDefault();
          e.stopPropagation();
          onDelete();
        }}
      >
        <Trash2 className="size-3.5" aria-hidden="true" />
      </Button>
    </DropdownMenuItem>
  );
}
